import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Search, RefreshCw, Filter, Mail, Building2, Download } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  quote_sent: "Quote Sent",
  won: "Won",
  closed: "Closed",
};

const STATUS_ORDER = ["new", "contacted", "quote_sent", "won", "closed"];

const StatusPill = ({ status }) => {
  const key = status || "new";
  const color = {
    new: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40",
    contacted: "bg-blue-500/15 text-blue-300 border-blue-400/40",
    quote_sent: "bg-purple-500/15 text-purple-300 border-purple-400/40",
    won: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
    closed: "bg-white/10 text-white/50 border-white/20",
  }[key] || "bg-white/10 text-white/60 border-white/20";
  return (
    <span className={`inline-flex items-center gap-1.5 border ${color} text-[10px] uppercase tracking-[0.18em] px-2.5 py-1`}>
      {STATUS_LABELS[key] || key}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// ---------- CSV utilities ----------
const CSV_HEADERS = [
  "Customer Name",
  "Company Name",
  "Email",
  "Phone",
  "Product Category",
  "Product Name",
  "Model Number / SKU",
  "Quantity",
  "Customer Message",
  "Status",
  "Internal Admin Notes",
  "Date Submitted",
  "Time Submitted",
];

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const submittedDate = (iso) => {
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};
const submittedTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  } catch {
    return "";
  }
};

const rowsToCsv = (rows, kind) => {
  const lines = [CSV_HEADERS.map(csvEscape).join(",")];
  rows.forEach((r) => {
    const items = r.items || [];
    const categories = items.map((it) => it.category || "").join(" | ");
    const names = items.map((it) => it.name || "").join(" | ");
    const skus = items.map((it) => it.product_id || "").join(" | ");
    const qtys = items.map((it) => (it.quantity ?? 1)).join(" | ");
    const message = kind === "contacts"
      ? [r.subject ? `Subject: ${r.subject}` : "", r.message || ""].filter(Boolean).join("\n")
      : (r.message || "");
    const row = [
      r.full_name || "",
      r.hotel_name || "",
      r.email || "",
      r.phone || "",
      categories,
      names,
      skus,
      qtys,
      message,
      r.status || "new",
      r.admin_notes || "",
      submittedDate(r.created_at),
      submittedTime(r.created_at),
    ];
    lines.push(row.map(csvEscape).join(","));
  });
  return lines.join("\r\n");
};

const downloadCsv = (rows, kind) => {
  const csv = rowsToCsv(rows, kind);
  const today = submittedDate(new Date().toISOString());
  const label = kind === "contacts" ? "contact-inbox" : "quote-inbox";
  const filename = `hospitality-hub-${label}-${today}.csv`;
  // Prepend BOM so Excel opens UTF-8 correctly
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AdminQuoteInbox = () => {
  const { logout, authHeader, API, admin } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ quotes: {}, contacts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDir, setSortDir] = useState("newest");

  useEffect(() => {
    document.title = "Quote Inbox | Hospitality Hub Admin";
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [q, c, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers: authHeader() }),
        axios.get(`${API}/admin/contacts`, { headers: authHeader() }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Failed to load. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const source = tab === "quotes" ? quotes : contacts;
  const statsForTab = tab === "quotes" ? stats.quotes || {} : stats.contacts || {};

  const filtered = useMemo(() => {
    let list = [...source];
    if (statusFilter !== "all") list = list.filter((d) => (d.status || "new") === statusFilter);
    const s = search.trim().toLowerCase();
    if (s) {
      list = list.filter((d) => {
        const productBlob = (d.items || []).map((it) => `${it.name} ${it.category} ${it.product_id}`).join(" ").toLowerCase();
        return (
          (d.full_name || "").toLowerCase().includes(s) ||
          (d.hotel_name || "").toLowerCase().includes(s) ||
          (d.email || "").toLowerCase().includes(s) ||
          (d.phone || "").toLowerCase().includes(s) ||
          (d.subject || "").toLowerCase().includes(s) ||
          (d.message || "").toLowerCase().includes(s) ||
          productBlob.includes(s)
        );
      });
    }
    list.sort((a, b) => {
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortDir === "newest" ? db - da : da - db;
    });
    return list;
  }, [source, statusFilter, search, sortDir]);

  const productSummary = (item) => {
    if (item.items && item.items.length > 0) {
      const first = item.items[0];
      const extra = item.items.length - 1;
      return `${first.name}${extra > 0 ? ` +${extra}` : ""}`;
    }
    return item.subject || "-";
  };

  return (
    <main data-testid="admin-inbox-page" className="min-h-screen bg-[#0E0E0E] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0E0E0E] sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center">
              <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Admin</p>
              <p className="font-serif-display text-lg leading-tight">Quote Inbox</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-white/50">{admin?.email}</span>
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={() => { logout(); navigate("/admin/login", { replace: true }); }}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-400 hover:text-red-400 px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-white/10 mb-8">
          {[
            ["quotes", "Quote Requests", quotes.length],
            ["contacts", "Contact Enquiries", contacts.length],
          ].map(([key, label, count]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`pb-4 -mb-px border-b-2 text-xs uppercase tracking-[0.22em] transition-colors ${
                tab === key ? "border-[#D4AF37] text-white" : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {label} <span className="ml-2 text-[10px] text-white/40">({count})</span>
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-10">
          {[["total", "Total"], ...STATUS_ORDER.map((s) => [s, STATUS_LABELS[s]])].map(([k, label]) => (
            <div
              key={k}
              data-testid={`admin-stat-${k}`}
              className={`bg-white/[0.03] border border-white/10 p-5 ${k === "total" ? "border-[#D4AF37]/40" : ""}`}
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</p>
              <p className={`mt-2 font-serif-display text-3xl ${k === "total" ? "text-[#D4AF37]" : "text-white"}`}>
                {statsForTab[k] ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, email, product…"
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#D4AF37] outline-none py-3 pl-10 pr-4 text-sm placeholder:text-white/30"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/40" />
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/[0.03] border border-white/10 focus:border-[#D4AF37] outline-none py-3 px-3 text-xs uppercase tracking-[0.18em]"
            >
              <option value="all">All statuses</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <select
            data-testid="admin-sort"
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            className="bg-white/[0.03] border border-white/10 focus:border-[#D4AF37] outline-none py-3 px-3 text-xs uppercase tracking-[0.18em]"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <button
            data-testid="admin-export-filtered"
            onClick={() => downloadCsv(filtered, tab)}
            disabled={filtered.length === 0}
            title={`Export ${filtered.length} filtered ${tab === "quotes" ? "quote" : "contact"} record(s)`}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 px-4 text-[10px] uppercase tracking-[0.2em] transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Export Filtered ({filtered.length})
          </button>
          <button
            data-testid="admin-export-all"
            onClick={() => downloadCsv(source, tab)}
            disabled={source.length === 0}
            title={`Export all ${source.length} ${tab === "quotes" ? "quote" : "contact"} record(s)`}
            className="inline-flex items-center gap-2 border border-white/25 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 px-4 text-[10px] uppercase tracking-[0.2em] transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Export All ({source.length})
          </button>
          <p className="text-xs text-white/50 ml-auto">Showing <span className="text-white">{filtered.length}</span> of {source.length}</p>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 py-10 text-center">Loading…</p>
        ) : error ? (
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 py-10 text-center">{error}</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-empty-state" className="text-xs uppercase tracking-[0.3em] text-white/40 py-16 text-center border border-dashed border-white/10">
            No {tab === "quotes" ? "quote requests" : "contact enquiries"} match your filters.
          </p>
        ) : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-white/50 bg-white/[0.03] border-b border-white/10">
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4 hidden md:table-cell">Company</th>
                  <th className="py-3 px-4 hidden lg:table-cell">Contact</th>
                  <th className="py-3 px-4">{tab === "quotes" ? "Product" : "Subject"}</th>
                  <th className="py-3 px-4 hidden sm:table-cell">Submitted</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    data-testid={`admin-row-${item.id}`}
                    onClick={() => navigate(`/admin/quotes/${tab === "quotes" ? "q" : "c"}/${item.id}`)}
                    className="border-b border-white/5 hover:bg-white/[0.04] transition-colors cursor-pointer"
                  >
                    <td className="py-4 px-4">
                      <p className="text-white font-medium">{item.full_name || "-"}</p>
                      <p className="text-xs text-white/40 mt-0.5 sm:hidden">{formatDate(item.created_at)}</p>
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell text-white/70">
                      <span className="inline-flex items-center gap-1.5">
                        {item.hotel_name ? <Building2 className="w-3.5 h-3.5 text-white/30" /> : null}
                        {item.hotel_name || "-"}
                      </span>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell text-white/60 text-xs">
                      <p className="truncate max-w-[220px]">{item.email}</p>
                      <p className="text-white/40">{item.phone || ""}</p>
                    </td>
                    <td className="py-4 px-4 text-white/80">
                      <p className="truncate max-w-[280px]">{productSummary(item)}</p>
                      {tab === "quotes" && item.items?.length > 0 && (
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mt-1">
                          {item.items.reduce((a, it) => a + (it.quantity || 1), 0)} units · {item.items.length} sku(s)
                        </p>
                      )}
                    </td>
                    <td className="py-4 px-4 hidden sm:table-cell text-white/50 text-xs">{formatDate(item.created_at)}</td>
                    <td className="py-4 px-4"><StatusPill status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-10 text-[10px] uppercase tracking-[0.2em] text-white/30">
          Signed in as {admin?.email} · Private admin dashboard · <Link to="/" className="hover:text-[#D4AF37]">Back to site</Link>
        </p>
      </div>
    </main>
  );
};

export default AdminQuoteInbox;
